import React, { useState, useEffect, useMemo } from 'react';
import { 
  Plus, 
  Search, 
  MapPin,
  User,
  Calendar,
  Download,
  Trash2,
  Eye,
  CreditCard,
  CheckCircle2,
  Clock,
  RefreshCw,
  Square,
  X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { landSalesAPI, clientsAPI } from '../lib/api';
import { formatCurrency, formatDate, cn } from '../lib/utils';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const LandSales = () => {
  const [sales, setSales] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Modal states
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isPaymentFormOpen, setIsPaymentFormOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    title: '',
    surface_area: '',
    location: '',
    district: '',
    total_price: '',
    currency: 'XOF',
    buyer_id: '',
    num_installments: 1,
    notes: ''
  });

  const [paymentData, setPaymentData] = useState({
    amount: '',
    currency: 'XOF',
    payment_date: new Date(),
    payment_method: 'especes',
    reference: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [salesRes, clientsRes] = await Promise.all([
        landSalesAPI.getAll(),
        clientsAPI.getAll()
      ]);
      setSales(salesRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  // Stats
  const stats = useMemo(() => {
    const total = sales.length;
    const enCours = sales.filter(s => s.status === 'en_cours').length;
    const complete = sales.filter(s => s.status === 'complete').length;
    const totalValue = sales.reduce((sum, s) => sum + (s.total_price || 0), 0);
    const totalPaid = sales.reduce((sum, s) => sum + (s.total_paid || 0), 0);
    
    return { total, enCours, complete, totalValue, totalPaid };
  }, [sales]);

  // Filtered sales
  const filteredSales = useMemo(() => {
    return sales.filter(sale => {
      const matchesSearch = 
        sale.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        sale.buyer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        sale.location?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        sale.reference?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || sale.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    });
  }, [sales, searchQuery, statusFilter]);

  // Buyers (clients who can buy)
  const buyers = useMemo(() => {
    return clients.filter(c => c.client_type === 'acheteur' || c.client_type === 'proprietaire');
  }, [clients]);

  const handleOpenForm = () => {
    setFormData({
      title: '',
      surface_area: '',
      location: '',
      district: '',
      total_price: '',
      currency: 'XOF',
      buyer_id: '',
      num_installments: 1,
      notes: ''
    });
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.surface_area || !formData.total_price || !formData.buyer_id) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setSaving(true);
    try {
      const submitData = {
        ...formData,
        surface_area: parseFloat(formData.surface_area),
        total_price: parseFloat(formData.total_price),
        num_installments: parseInt(formData.num_installments)
      };

      await landSalesAPI.create(submitData);
      toast.success('Vente de terrain créée avec succès');
      handleCloseForm();
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la création';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDetail = async (sale) => {
    try {
      const response = await landSalesAPI.getById(sale.id);
      setSelectedSale(response.data);
      setIsDetailOpen(true);
    } catch (error) {
      toast.error('Erreur lors du chargement des détails');
    }
  };

  const handleOpenPaymentForm = (sale) => {
    setSelectedSale(sale);
    setPaymentData({
      amount: '',
      currency: sale.currency || 'XOF',
      payment_date: new Date(),
      payment_method: 'especes',
      reference: '',
      notes: ''
    });
    setIsPaymentFormOpen(true);
  };

  const handleSubmitPayment = async (e) => {
    e.preventDefault();
    
    if (!paymentData.amount) {
      toast.error('Veuillez entrer un montant');
      return;
    }

    setSaving(true);
    try {
      const submitData = {
        ...paymentData,
        amount: parseFloat(paymentData.amount),
        payment_date: paymentData.payment_date.toISOString()
      };

      const response = await landSalesAPI.addPayment(selectedSale.id, submitData);
      
      if (response.data.sale_status === 'complete') {
        toast.success('Paiement enregistré - Vente soldée !');
      } else {
        toast.success(`Paiement enregistré - Reste: ${formatCurrency(response.data.remaining, paymentData.currency)}`);
      }
      
      setIsPaymentFormOpen(false);
      fetchData();
      
      // Refresh detail if open
      if (isDetailOpen && selectedSale) {
        handleOpenDetail(selectedSale);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de l\'enregistrement';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPdf = async (paymentId, receiptNumber) => {
    try {
      const response = await landSalesAPI.downloadPaymentPdf(paymentId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `attestation_${receiptNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Attestation téléchargée');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleDelete = async () => {
    if (!selectedSale) return;
    
    try {
      await landSalesAPI.delete(selectedSale.id);
      toast.success('Vente supprimée');
      setIsDeleteOpen(false);
      setSelectedSale(null);
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la suppression';
      toast.error(message);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      'en_cours': { label: 'En cours', className: 'bg-yellow-100 text-yellow-700' },
      'complete': { label: 'Soldée', className: 'bg-green-100 text-green-700' },
      'annule': { label: 'Annulée', className: 'bg-red-100 text-red-700' }
    };
    const { label, className } = config[status] || config['en_cours'];
    return <Badge className={className}>{label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#1B3A4B]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ventes de Terrains</h1>
          <p className="text-gray-500">Gestion des ventes avec paiement échelonné</p>
        </div>
        <Button 
          onClick={handleOpenForm}
          className="bg-[#1B3A4B] hover:bg-[#142D3A]"
          data-testid="add-land-sale-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle vente
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Square className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total ventes</p>
                <p className="text-xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">En cours</p>
                <p className="text-xl font-bold">{stats.enCours}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Soldées</p>
                <p className="text-xl font-bold">{stats.complete}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <MapPin className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Valeur totale</p>
                <p className="text-lg font-bold">{formatCurrency(stats.totalValue, 'XOF')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#1B3A4B]/10 rounded-lg">
                <CreditCard className="w-5 h-5 text-[#1B3A4B]" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total encaissé</p>
                <p className="text-lg font-bold">{formatCurrency(stats.totalPaid, 'XOF')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Rechercher par terrain, acheteur, localisation..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-land-sales"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]" data-testid="status-filter">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="en_cours">En cours</SelectItem>
            <SelectItem value="complete">Soldées</SelectItem>
            <SelectItem value="annule">Annulées</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Sales List */}
      <Card>
        <CardContent className="p-0">
          {filteredSales.length === 0 ? (
            <div className="text-center py-12">
              <MapPin className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500">Aucune vente de terrain trouvée</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-4 font-medium text-gray-600">Référence</th>
                    <th className="text-left p-4 font-medium text-gray-600">Terrain</th>
                    <th className="text-left p-4 font-medium text-gray-600">Acheteur</th>
                    <th className="text-left p-4 font-medium text-gray-600">Prix total</th>
                    <th className="text-left p-4 font-medium text-gray-600">Payé</th>
                    <th className="text-left p-4 font-medium text-gray-600">Reste</th>
                    <th className="text-left p-4 font-medium text-gray-600">Paiements</th>
                    <th className="text-left p-4 font-medium text-gray-600">Statut</th>
                    <th className="text-right p-4 font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredSales.map((sale) => (
                    <tr key={sale.id} className="hover:bg-gray-50" data-testid={`land-sale-row-${sale.id}`}>
                      <td className="p-4">
                        <span className="font-mono text-sm text-gray-500">{sale.reference}</span>
                      </td>
                      <td className="p-4">
                        <div>
                          <p className="font-medium text-gray-900">{sale.title}</p>
                          <p className="text-sm text-gray-500">{sale.surface_area} m² - {sale.location}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-400" />
                          <span>{sale.buyer_name}</span>
                        </div>
                      </td>
                      <td className="p-4 font-medium">
                        {formatCurrency(sale.total_price, sale.currency)}
                      </td>
                      <td className="p-4 text-green-600 font-medium">
                        {formatCurrency(sale.total_paid || 0, sale.currency)}
                      </td>
                      <td className="p-4 text-orange-600 font-medium">
                        {formatCurrency(sale.remaining_amount || sale.total_price, sale.currency)}
                      </td>
                      <td className="p-4">
                        <Badge variant="outline">{sale.payments_count || 0}/{sale.num_installments}</Badge>
                      </td>
                      <td className="p-4">
                        {getStatusBadge(sale.status)}
                      </td>
                      <td className="p-4">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenDetail(sale)}
                            title="Voir détails"
                            data-testid={`view-sale-${sale.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {sale.status === 'en_cours' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenPaymentForm(sale)}
                              title="Ajouter un paiement"
                              className="text-[#1B3A4B]"
                              data-testid={`add-payment-${sale.id}`}
                            >
                              <CreditCard className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setSelectedSale(sale);
                              setIsDeleteOpen(true);
                            }}
                            title="Supprimer"
                            className="text-red-600"
                            data-testid={`delete-sale-${sale.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Sale Form Modal */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#1B3A4B]" />
              Nouvelle vente de terrain
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="title">Titre/Nom du terrain *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Ex: Terrain Sotuba ACI"
                data-testid="land-title-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="surface_area">Superficie (m²) *</Label>
                <Input
                  id="surface_area"
                  type="number"
                  value={formData.surface_area}
                  onChange={(e) => setFormData({ ...formData, surface_area: e.target.value })}
                  placeholder="500"
                  data-testid="land-surface-input"
                />
              </div>
              <div>
                <Label htmlFor="district">Quartier</Label>
                <Input
                  id="district"
                  value={formData.district}
                  onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                  placeholder="Ex: ACI 2000"
                  data-testid="land-district-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="location">Localisation/Adresse *</Label>
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="Ex: Bamako, Mali"
                data-testid="land-location-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="total_price">Prix de vente *</Label>
                <Input
                  id="total_price"
                  type="number"
                  value={formData.total_price}
                  onChange={(e) => setFormData({ ...formData, total_price: e.target.value })}
                  placeholder="5000000"
                  data-testid="land-price-input"
                />
              </div>
              <div>
                <Label htmlFor="currency">Devise</Label>
                <Select
                  value={formData.currency}
                  onValueChange={(value) => setFormData({ ...formData, currency: value })}
                >
                  <SelectTrigger data-testid="land-currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="XOF">FCFA (XOF)</SelectItem>
                    <SelectItem value="EUR">Euro (EUR)</SelectItem>
                    <SelectItem value="USD">Dollar (USD)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="buyer_id">Acheteur *</Label>
              <Select
                value={formData.buyer_id}
                onValueChange={(value) => setFormData({ ...formData, buyer_id: value })}
              >
                <SelectTrigger data-testid="land-buyer-select">
                  <SelectValue placeholder="Sélectionner un acheteur" />
                </SelectTrigger>
                <SelectContent>
                  {buyers.map((buyer) => (
                    <SelectItem key={buyer.id} value={buyer.id}>
                      {buyer.name} - {buyer.phone}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Clients de type "Acheteur" ou "Propriétaire"
              </p>
            </div>

            <div>
              <Label htmlFor="num_installments">Nombre de tranches prévues</Label>
              <Input
                id="num_installments"
                type="number"
                min="1"
                value={formData.num_installments}
                onChange={(e) => setFormData({ ...formData, num_installments: e.target.value })}
                placeholder="1"
                data-testid="land-installments-input"
              />
            </div>

            <div>
              <Label htmlFor="notes">Notes</Label>
              <Input
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Notes ou commentaires..."
                data-testid="land-notes-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseForm}>
                Annuler
              </Button>
              <Button 
                type="submit" 
                className="bg-[#1B3A4B] hover:bg-[#142D3A]"
                disabled={saving}
                data-testid="submit-land-sale-btn"
              >
                {saving ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Création...
                  </>
                ) : (
                  'Créer la vente'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Payment Modal */}
      <Dialog open={isPaymentFormOpen} onOpenChange={setIsPaymentFormOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-[#D4A373]" />
              Enregistrer un paiement
            </DialogTitle>
          </DialogHeader>
          
          {selectedSale && (
            <div className="p-3 bg-gray-50 rounded-lg mb-4">
              <p className="font-medium">{selectedSale.title}</p>
              <p className="text-sm text-gray-500">Acheteur: {selectedSale.buyer_name}</p>
              <div className="flex justify-between mt-2 text-sm">
                <span>Reste à payer:</span>
                <span className="font-bold text-orange-600">
                  {formatCurrency(selectedSale.remaining_amount || selectedSale.total_price, selectedSale.currency)}
                </span>
              </div>
            </div>
          )}
          
          <form onSubmit={handleSubmitPayment} className="space-y-4">
            <div>
              <Label htmlFor="pay_amount">Montant *</Label>
              <Input
                id="pay_amount"
                type="number"
                value={paymentData.amount}
                onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                placeholder="0"
                data-testid="payment-amount-input"
              />
            </div>

            <div>
              <Label>Date du paiement</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !paymentData.payment_date && "text-muted-foreground"
                    )}
                    data-testid="payment-date-btn"
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    {paymentData.payment_date 
                      ? format(paymentData.payment_date, 'PPP', { locale: fr })
                      : "Sélectionner une date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <CalendarComponent
                    mode="single"
                    selected={paymentData.payment_date}
                    onSelect={(date) => setPaymentData({ ...paymentData, payment_date: date || new Date() })}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <Label>Mode de paiement</Label>
              <Select
                value={paymentData.payment_method}
                onValueChange={(value) => setPaymentData({ ...paymentData, payment_method: value })}
              >
                <SelectTrigger data-testid="payment-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="especes">Espèces</SelectItem>
                  <SelectItem value="virement">Virement bancaire</SelectItem>
                  <SelectItem value="mobile_money">Mobile Money</SelectItem>
                  <SelectItem value="cheque">Chèque</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="pay_reference">Référence (optionnel)</Label>
              <Input
                id="pay_reference"
                value={paymentData.reference}
                onChange={(e) => setPaymentData({ ...paymentData, reference: e.target.value })}
                placeholder="N° chèque, référence virement..."
                data-testid="payment-reference-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsPaymentFormOpen(false)}>
                Annuler
              </Button>
              <Button 
                type="submit" 
                className="bg-[#1B3A4B] hover:bg-[#142D3A]"
                disabled={saving}
                data-testid="submit-payment-btn"
              >
                {saving ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  'Enregistrer'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Sale Detail Modal */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#1B3A4B]" />
                {selectedSale?.title}
              </div>
              {selectedSale && getStatusBadge(selectedSale.status)}
            </DialogTitle>
          </DialogHeader>
          
          {selectedSale && (
            <div className="space-y-6">
              {/* Sale Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Référence</p>
                  <p className="font-mono">{selectedSale.reference}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Superficie</p>
                  <p className="font-medium">{selectedSale.surface_area} m²</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Localisation</p>
                  <p>{selectedSale.location}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Quartier</p>
                  <p>{selectedSale.district || 'N/A'}</p>
                </div>
              </div>

              {/* Buyer Info */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500 mb-2">Acheteur</p>
                <p className="font-medium">{selectedSale.buyer_name}</p>
                <p className="text-sm text-gray-600">{selectedSale.buyer_phone}</p>
                <p className="text-sm text-gray-600">{selectedSale.buyer_address}</p>
              </div>

              {/* Financial Summary */}
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-gray-500">Prix total</p>
                    <p className="text-lg font-bold">{formatCurrency(selectedSale.total_price, selectedSale.currency)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-gray-500">Total payé</p>
                    <p className="text-lg font-bold text-green-600">{formatCurrency(selectedSale.total_paid || 0, selectedSale.currency)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-gray-500">Reste à payer</p>
                    <p className="text-lg font-bold text-orange-600">{formatCurrency(selectedSale.remaining_amount || selectedSale.total_price, selectedSale.currency)}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Payments List */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium">Historique des paiements</h3>
                  {selectedSale.status === 'en_cours' && (
                    <Button
                      size="sm"
                      onClick={() => {
                        setIsDetailOpen(false);
                        handleOpenPaymentForm(selectedSale);
                      }}
                      className="bg-[#1B3A4B] hover:bg-[#142D3A]"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Ajouter paiement
                    </Button>
                  )}
                </div>
                
                {selectedSale.payments && selectedSale.payments.length > 0 ? (
                  <div className="space-y-2">
                    {selectedSale.payments.map((payment, index) => (
                      <div key={payment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">#{payment.payment_number}</Badge>
                            <span className="font-medium">{formatCurrency(payment.amount, payment.currency)}</span>
                          </div>
                          <p className="text-sm text-gray-500">
                            {formatDate(payment.payment_date)} - {payment.payment_method?.replace('_', ' ')}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-right text-sm">
                            <p className="text-gray-500">Reste après: {formatCurrency(payment.remaining_after, payment.currency)}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDownloadPdf(payment.id, payment.receipt_number)}
                            title="Télécharger attestation"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-4">Aucun paiement enregistré</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette vente ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Tous les paiements associés seront également supprimés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-sale-btn"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LandSales;
